import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CameraIcon } from "../util/Icons";
import PlaygroundMenu from "./PlaygroundMenu";

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350;
const FRAME_SRC = "/img/pictureOverlays/60nr-photo-booth-26.png";

const BOOTH_SLOTS = [
  { id: "left", x: 0, y: 0, width: 710, height: 1260, zoomGroup: "left", adjustments: null },
  {
    id: "top-right",
    x: 721,
    y: 30,
    width: 360,
    height: 409,
    zoomGroup: "right",
    adjustments: { sepia: 0.9, contrast: 1.3, saturation: 1.2 },
  },
  {
    id: "mid-right",
    x: 721,
    y: 469,
    width: 360,
    height: 409,
    zoomGroup: "right",
    adjustments: { contrast: 1.4, saturation: 0 },
  },
  {
    id: "bottom-right",
    x: 721,
    y: 908,
    width: 360,
    height: 409,
    zoomGroup: "right",
    adjustments: { sepia: 0.18, contrast: 1.25, saturation: 1.25, hueRotate: -37 },
  },
];

export default function PhotoBooth() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const userImageRef = useRef(null);
  const frameImageRef = useRef(null);

  const [frameReady, setFrameReady] = useState(false);
  const [frameLoadError, setFrameLoadError] = useState(false);
  const [sourceImageName, setSourceImageName] = useState("photo-booth-60nr-2026.png");
  const [hasSourceImage, setHasSourceImage] = useState(false);
  const [zoomLeft, setZoomLeft] = useState(100);
  const [zoomRight, setZoomRight] = useState(100);

  const clampColor = useCallback((v) => {
    if (v < 0) return 0;
    if (v > 255) return 255;
    return v;
  }, []);

  const applySlotAdjustments = useCallback((ctx, slot) => {
    if (!slot?.adjustments) return;

    const { sepia = 0, contrast = 1, saturation = 1, hueRotate = 0 } = slot.adjustments;
    const imageData = ctx.getImageData(slot.x, slot.y, slot.width, slot.height);
    const px = imageData.data;

    for (let i = 0; i < px.length; i += 4) {
      const a = px[i + 3];
      if (a === 0) continue;

      let r = px[i];
      let g = px[i + 1];
      let b = px[i + 2];

      if (sepia > 0) {
        const sepiaR = 0.393 * r + 0.769 * g + 0.189 * b;
        const sepiaG = 0.349 * r + 0.686 * g + 0.168 * b;
        const sepiaB = 0.272 * r + 0.534 * g + 0.131 * b;
        r = r + (sepiaR - r) * sepia;
        g = g + (sepiaG - g) * sepia;
        b = b + (sepiaB - b) * sepia;
      }

      if (contrast !== 1) {
        r = (r - 128) * contrast + 128;
        g = (g - 128) * contrast + 128;
        b = (b - 128) * contrast + 128;
      }

      if (saturation !== 1) {
        const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        r = gray + (r - gray) * saturation;
        g = gray + (g - gray) * saturation;
        b = gray + (b - gray) * saturation;
      }

      if (hueRotate !== 0) {
        const rn = r / 255;
        const gn = g / 255;
        const bn = b / 255;
        const max = Math.max(rn, gn, bn);
        const min = Math.min(rn, gn, bn);
        const delta = max - min;
        const lightness = (max + min) / 2;

        let hue = 0;
        let sat = 0;

        if (delta !== 0) {
          sat = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
          switch (max) {
            case rn:
              hue = (gn - bn) / delta + (gn < bn ? 6 : 0);
              break;
            case gn:
              hue = (bn - rn) / delta + 2;
              break;
            default:
              hue = (rn - gn) / delta + 4;
              break;
          }
          hue *= 60;
        }

        hue = (hue + hueRotate) % 360;
        if (hue < 0) hue += 360;

        if (sat === 0) {
          r = lightness * 255;
          g = lightness * 255;
          b = lightness * 255;
        } else {
          const q = lightness < 0.5 ? lightness * (1 + sat) : lightness + sat - lightness * sat;
          const p = 2 * lightness - q;

          const hueToRgb = (t) => {
            let tt = t;
            if (tt < 0) tt += 1;
            if (tt > 1) tt -= 1;
            if (tt < 1 / 6) return p + (q - p) * 6 * tt;
            if (tt < 1 / 2) return q;
            if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
            return p;
          };

          const hk = hue / 360;
          r = hueToRgb(hk + 1 / 3) * 255;
          g = hueToRgb(hk) * 255;
          b = hueToRgb(hk - 1 / 3) * 255;
        }
      }

      px[i] = clampColor(Math.round(r));
      px[i + 1] = clampColor(Math.round(g));
      px[i + 2] = clampColor(Math.round(b));
    }

    ctx.putImageData(imageData, slot.x, slot.y);
  }, [clampColor]);

  const drawImageToSlot = useCallback((ctx, img, slot, zoomPercent) => {
    const zoom = Math.max(10, zoomPercent) / 100;
    const coverScale = Math.max(slot.width / img.width, slot.height / img.height) * zoom;
    const drawWidth = img.width * coverScale;
    const drawHeight = img.height * coverScale;
    const drawX = slot.x + (slot.width - drawWidth) / 2;
    const drawY = slot.y + (slot.height - drawHeight) / 2;

    ctx.save();
    ctx.beginPath();
    ctx.rect(slot.x, slot.y, slot.width, slot.height);
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();
  }, []);

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    canvasRef.current.width = CANVAS_WIDTH;
    canvasRef.current.height = CANVAS_HEIGHT;
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Fallback background while waiting for frame overlay.
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const userImg = userImageRef.current;
    if (userImg) {
      BOOTH_SLOTS.forEach((slot) => {
        const zoom = slot.zoomGroup === "left" ? zoomLeft : zoomRight;
        drawImageToSlot(ctx, userImg, slot, zoom);
        applySlotAdjustments(ctx, slot);
      });
    }

    if (frameImageRef.current) {
      ctx.filter = "none";
      ctx.drawImage(frameImageRef.current, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }
  }, [zoomLeft, zoomRight, drawImageToSlot, applySlotAdjustments]);

  useEffect(() => {
    const frameImg = new Image();
    frameImg.crossOrigin = "anonymous";
    frameImg.src = FRAME_SRC;
    frameImg.onload = () => {
      frameImageRef.current = frameImg;
      setFrameReady(true);
      setFrameLoadError(false);
      drawCanvas();
    };
    frameImg.onerror = () => {
      frameImageRef.current = null;
      setFrameReady(false);
      setFrameLoadError(true);
      drawCanvas();
    };
  }, [drawCanvas]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas, hasSourceImage]);

  function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.src = objUrl;
    img.onload = () => {
      userImageRef.current = img;
      setHasSourceImage(true);
      setSourceImageName((file.name || "photo-booth-upload").replace(/\.[^/.]+$/, "") + "-photo-booth.png");
      drawCanvas();
      URL.revokeObjectURL(objUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
    };
  }

  function exportToBlob(callback) {
    if (!hasSourceImage || !canvasRef.current) return;
    drawCanvas();
    canvasRef.current.toBlob(
      (blob) => {
        if (!blob) return;
        callback(blob);
      },
      "image/png",
      1
    );
  }

  function handleDownload() {
    exportToBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sourceImageName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }

  function handlePost() {
    exportToBlob((blob) => {
      const file = new File([blob], sourceImageName, { type: blob.type });
      navigate("/", { state: { createdImage: file } });
    });
  }

  return (
    <div className="min-h-screen bg-white pb-4 md:pb-10">
      <PlaygroundMenu />

      <div className="max-w-2xl mx-auto px-0 py-1 md:px-3 md:py-4">
        <div className="flex items-center mb-2 md:mb-3 mt-1">
          <h1 className="text-lg md:text-2xl font-bold text-slate-800 m-0">Photo Booth</h1>
        </div>

        <div className="bg-white rounded-none md:rounded-xl shadow-sm border-y md:border border-slate-200 p-3 md:p-4 lg:p-5">
          <div className="flex flex-col gap-3">
            <div className="">
              <label
                htmlFor="photo-booth-upload"
                className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-white text-sm font-semibold cursor-pointer"
                style={{ background: "linear-gradient(135deg, #2563eb 0%, #4338ca 100%)" }}
              >
                <CameraIcon size={20} />
                Upload Your Photo
              </label>
              <input id="photo-booth-upload" type="file" accept="image/*" className="hidden" onInput={handleUpload} />
            </div>

            {frameLoadError && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 text-sm">
                Frame image not found at <code>{FRAME_SRC}</code>. Add the PNG there to render the final overlay.
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-100 p-2 max-w-lg mx-auto w-full">
              <canvas ref={canvasRef} className="w-full h-auto rounded-lg bg-black" />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-slate-700">Left Image Zoom: {zoomLeft}%</span>
                <input type="range" min={60} max={180} step={1} value={zoomLeft} onChange={(e) => setZoomLeft(e.target.valueAsNumber)} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-slate-700">Right Images Zoom: {zoomRight}%</span>
                <input type="range" min={60} max={180} step={1} value={zoomRight} onChange={(e) => setZoomRight(e.target.valueAsNumber)} />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                type="button"
                disabled={!hasSourceImage || !frameReady}
                onClick={handleDownload}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${hasSourceImage && frameReady ? "bg-slate-900 text-white hover:bg-slate-700" : "bg-slate-300 text-slate-500 cursor-not-allowed"
                  }`}
              >
                Download Image
              </button>
              <button
                type="button"
                disabled={!hasSourceImage || !frameReady}
                onClick={handlePost}
                className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${hasSourceImage && frameReady ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-slate-300 text-slate-500 cursor-not-allowed"
                  }`}
              >
                Post to Feed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
